import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import {
  Card,
  Container,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from 'reactstrap';

import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Meta from 'components/App/Meta';
import AppInputValidation from 'components/App/InputValidation';
import AppSelectValidation from 'components/App/SelectValidation';
import Pagination from 'components/Pagination';

import {
  COMPLAINT_STATUS,
  COMPLAINT_TYPE_LIST,
  LOCATIONS,
  themeColor,
} from 'constants/index';
import cn from 'utils/classnames';
import getTotalPage from 'utils/totalPage';
import { getParamsFormObject } from 'utils';

import DataTableComplaintList from './DataTableComplaintList';
import styles from './ManagerComplaint.module.scss';

const breadcrumbList = [
  {
    name: 'Quản lý khiếu nại',
    isActive: true,
    link: LOCATIONS.MANAGER_COMPLAINT,
  },
];

const TAB = {
  ALL: '',
  PENDING: COMPLAINT_STATUS.PENDING,
  RESOLVED: COMPLAINT_STATUS.RESOLVED,
  SKIPPED: COMPLAINT_STATUS.SKIPPED,
};

const NAV_TAB = [
  {
    title: 'Tất cả',
    value: TAB.ALL,
  },
  {
    title: 'Đang chờ xử lý',
    value: TAB.PENDING,
  },
  {
    title: 'Đã xử lý',
    value: TAB.RESOLVED,
  },
  {
    title: 'Từ chối',
    value: TAB.SKIPPED,
  },
];

const DEFAULT_LIMIT = 20;

const TYPE_OPTIONS = [
  { label: 'Tất cả phân loại', value: '' },
  ...COMPLAINT_TYPE_LIST,
];

function ManagerComplaint(props) {
  const {
    managerComplaints,
    totalManagerComplaints,
    isLoadingGetManagerComplaints,
    actionGetManagerComplaints,
  } = props;

  const history = useHistory();

  const urlParams = useMemo(
    () => new URLSearchParams(history.location.search),
    [history.location.search],
  );

  const urlPage = useMemo(
    () => Number(urlParams.get('page') || 1),
    [urlParams],
  );

  const searchQueries = useMemo(() => ({
    page: urlPage,
    limit: Number(urlParams.get('limit') || DEFAULT_LIMIT),
    q: urlParams.get('q') || '',
    status: urlParams.get('status') || '',
    type: urlParams.get('type') || '',
  }), [urlPage, urlParams]);

  const activeTab = searchQueries.status || TAB.ALL;

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      q: searchQueries.q,
      type: searchQueries.type,
    },
    onSubmit: (values) => {
      history.push({
        search: getParamsFormObject({
          q: values.q?.trim() || '',
          status: searchQueries.status || '',
          type: values.type || '',
          page: 1,
          limit: DEFAULT_LIMIT,
        }),
      });
    },
  });

  const onChangeFilterOption = useCallback(
    (e) => {
      validation.setFieldValue(e.name, e.value, true);
    },
    [validation],
  );

  const onResetForm = useCallback(() => {
    validation.resetForm({
      values: {
        q: '',
        type: '',
      },
    });

    history.push({
      search: getParamsFormObject({
        status: searchQueries.status || '',
        page: 1,
        limit: DEFAULT_LIMIT,
      }),
    });
  }, [history, searchQueries.status, validation]);

  const onChangeTab = useCallback(
    (tabValue) => () => {
      history.push({
        search: getParamsFormObject({
          ...searchQueries,
          status: tabValue,
          page: 1,
        }),
      });
    },
    [history, searchQueries],
  );

  const onChangePage = useCallback(
    (selectedItem) => {
      history.push({
        search: getParamsFormObject({
          ...searchQueries,
          page: selectedItem.selected + 1,
        }),
      });
    },
    [history, searchQueries],
  );

  const onOpenDetail = useCallback(
    (complaint) => {
      history.push(`${LOCATIONS.MANAGER_COMPLAINT}/${complaint._id}`);
    },
    [history],
  );

  // Dispatch action to fetch complaints via Redux Saga
  useEffect(() => {
    const payload = {
      page: searchQueries.page,
      limit: searchQueries.limit || DEFAULT_LIMIT,
      ...(searchQueries.q ? { q: searchQueries.q } : {}),
      ...(searchQueries.status ? { status: searchQueries.status } : {}),
      ...(searchQueries.type ? { type: searchQueries.type } : {}),
    };

    actionGetManagerComplaints(payload);
  }, [actionGetManagerComplaints, searchQueries]);

  const pageCount = getTotalPage(totalManagerComplaints, searchQueries.limit || DEFAULT_LIMIT);

  return (
    <div className={`page-content ${styles.Page}`}>
      <Meta name="Quản lý khiếu nại" />

      <Container fluid>
        {/* Render Breadcrumb */}
        <Breadcrumb
          pageTitle="Quản lý khiếu nại"
          breadcrumbList={breadcrumbList}
        />

        {/* Search & Filter Card */}
        <Card className="Section">
          <form onSubmit={validation.handleSubmit}>
            <Row className="Section__Search__Filter">
              <div className="mb-24 flex-1">
                <Label className="Section__Search__Label">Tìm kiếm</Label>

                <div className="Section__Search__Container">
                  <AppInputValidation
                    name="q"
                    id="q"
                    type="text"
                    placeholder="Tìm theo mã khiếu nại và email"
                    validation={validation}
                    inputClassName={cn('Section__Search__Input')}
                  />

                  <i className="fas fa-search Section__Search__Icon__Search" />
                </div>
              </div>

              <div className="flex-2">
                <Label className="Section__Search__Label">Bộ lọc</Label>

                <div className="Section__Filter__Container">
                  <AppSelectValidation
                    placeholder="Phân loại"
                    name="type"
                    key="type"
                    options={TYPE_OPTIONS}
                    validation={validation}
                    value={validation.values.type}
                    onChange={onChangeFilterOption}
                    selectedClassName={styles.FilterInput}
                  />
                </div>
              </div>
            </Row>

            <div className="d-flex mt-24">
              <Button
                outline
                classes="Section__Search__Button__ReType"
                color={themeColor.LIGHT}
                onClick={onResetForm}
              >
                Nhập lại
              </Button>

              <Button
                classes="Section__Search__Button__Search"
                type="submit"
              >
                Tìm kiếm
              </Button>
            </div>
          </form>
        </Card>

        {/* Data Table Card */}
        <Card className="Section">
          <div className="d-flex Table__Header mb-3">
            <Nav pills className="col-md-8">
              {NAV_TAB.map((tab) => (
                <NavItem key={tab.title} className="col-md-3">
                  <NavLink
                    className={activeTab === tab.value ? 'active' : ''}
                    onClick={onChangeTab(tab.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span>{tab.title}</span>
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          </div>

          <Label className="Table__Header__Total">
            {`${totalManagerComplaints} khiếu nại`}
          </Label>

          <TabContent activeTab={activeTab || 'ALL'} className="Table__Body">
            <TabPane tabId={activeTab || 'ALL'}>
              <DataTableComplaintList
                isLoading={isLoadingGetManagerComplaints}
                complaints={managerComplaints}
                onOpenDetail={onOpenDetail}
              />
            </TabPane>
          </TabContent>
        </Card>

        {/* Pagination */}
        {totalManagerComplaints > 0 && (
          <Pagination
            page={urlPage}
            pageCount={pageCount}
            onChangePage={onChangePage}
          />
        )}
      </Container>
    </div>
  );
}

ManagerComplaint.propTypes = {
  isLoadingGetManagerComplaints: PropTypes.bool,
  managerComplaints: PropTypes.arrayOf(PropTypes.shape({})),
  totalManagerComplaints: PropTypes.number,
  actionGetManagerComplaints: PropTypes.func.isRequired,
};

ManagerComplaint.defaultProps = {
  isLoadingGetManagerComplaints: false,
  managerComplaints: [],
  totalManagerComplaints: 0,
};

export default ManagerComplaint;
